import AsyncStorage from '@react-native-async-storage/async-storage'
import LZString from "lz-string"
import { Platform } from "react-native";
import { MMKV } from 'react-native-mmkv'
import { SimpleIDB } from './storageIndexDB'
import * as FileSystem from 'expo-file-system';

// ==========================================
// 1. CONFIGURACIÓN DE STORAGES (MAIN vs SESSION)
// ==========================================

// Main DB: Datos permanentes de negocio (Forms, Items offline, Integraciones, Tasks)
export const mainDB = Platform.OS === "web" ? SimpleIDB : new MMKV();
export const DB = mainDB; // compatibilidad hacia atrás
const USE_MMKV = true;

if (Platform.OS === "web") {
    SimpleIDB.initialize();
}

// Session Cache DB:
// - En Mobile (Android): instancia de MMKV en la carpeta /cache/ para que "Borrar Caché" del sistema
//   limpie usuario y navegación sin tocar los datos de negocio (items offline).
// - En Web: Usa la MISMA base IndexedDB (SimpleIDB) para que cuando el usuario cierre la pestaña
//   o ventana y vuelva al día siguiente, continúe exactamente donde lo dejó.
let cachePath: string | undefined = undefined;
if (Platform.OS === 'android' && FileSystem && FileSystem.cacheDirectory) {
    try {
        cachePath = FileSystem.cacheDirectory.replace('file://', '');
        if (!cachePath.endsWith('/')) cachePath += '/';
        cachePath += 'mmkv_session';
    } catch (e) {
        console.warn("Error al calcular cachePath para sessionCacheDB:", e);
    }
}

let sessionCacheMMKV: any = null;
if (Platform.OS !== "web") {
    try {
        sessionCacheMMKV = new MMKV({ id: "session-cache", path: cachePath });
    } catch (e) {
        console.warn("No se pudo iniciar MMKV en cachePath, usando ruta por defecto:", e);
        sessionCacheMMKV = new MMKV({ id: "session-cache" });
    }
}

export const sessionCacheDB = Platform.OS === "web" ? SimpleIDB : sessionCacheMMKV;

// ==========================================
// 2. OBJETOS DE ALMACENAMIENTO
// ==========================================

export const sessionCacheStorage = {
    setItem: (key: string, data: any) => sessionCacheDB.set(key, data),
    getItem: (key: string) => sessionCacheDB.getString(key),
    removeItem: (key: string) => sessionCacheDB.delete(key),
    clearAll: () => {
        if (Platform.OS === "web") {
            return clearSessionAndNavigation();
        } else {
            return sessionCacheDB.clearAll();
        }
    },
};

export const Storage = {
    // @ts-ignore
    setItem: (key, data) => USE_MMKV ? mainDB.set(key, data) : AsyncStorage.setItem(key, data),
    // @ts-ignore
    getItem: (key) => USE_MMKV ? mainDB.getString(key) : AsyncStorage.getItem(key),
    // @ts-ignore
    removeItem: (key) => USE_MMKV ? mainDB.delete(key) : AsyncStorage.removeItem(key),
    // @ts-ignore
    clearAll: () => USE_MMKV ? mainDB.clearAll() : AsyncStorage.clear(),
    // @ts-ignore
    removeLocal: (key: string) => removeLocal(key),
    // @ts-ignore
    saveLocal: (key: string, value: any) => saveLocal(key, value),
    // @ts-ignore
    loadLocal: (key: string) => loadLocal(key),
    clearSessionAndNavigation: () => clearSessionAndNavigation(),
}

const compress = (c: string) => { return Platform.OS == "web1" ? LZString.compressToUTF16(c) : c; };
const decompress = (c: string) => { return Platform.OS == "web1" ? LZString.decompressFromUTF16(c) : c; };

const keyNew = (key: string) => { return "v3-".concat(key) }

// ==========================================
// 3. MAIN STORAGE (Forms, Items, Tasks, etc.)
// ==========================================

export const getItem = async (key: string): Promise<string | null> => {
    try {
        let keyOriginal = key;
        key = keyNew(key);
        let store = "";
        let numberOfParts: string | number = await Storage.getItem(key);
        if (typeof (numberOfParts) === 'undefined' || numberOfParts === null) {
            // Migración de data anterior si existe
            let originalValue = null;
            originalValue = await Storage.getItem(keyOriginal + "-part0");
            if (originalValue && originalValue != "") {
                numberOfParts = await Storage.getItem(keyOriginal);
                // @ts-ignore
                numberOfParts = parseInt(numberOfParts);
                for (let i = 0; i < numberOfParts; i++) { store += await Storage.getItem(keyOriginal + "-part" + i); }
                await setItem(keyOriginal, JSON.parse(store))
                return await JSON.parse(store);
            } else {
                originalValue = await Storage.getItem(keyOriginal);
                if (originalValue || originalValue != null) {
                    await setItem(keyOriginal, originalValue)
                    return originalValue;
                } else
                    return null;
            }
        } else
            // @ts-ignore
            numberOfParts = parseInt(numberOfParts);
        for (let i = 0; i < numberOfParts; i++) { store += decompress(await Storage.getItem(key + "-part" + i)); }
        if (store === "")
            return null;

        return JSON.parse(store);
    }
    catch (error) {
        console.log("Could not get [" + key + "] from store.");
        console.log(error);
        return null;
    }
};

export const setItem = async (key: string, data: string) => {
    try {
        key = keyNew(key);
        await removeItem(key);

        const store = JSON.stringify(data).match(/.{1,100000}/g);
        store.forEach((part, index) => {
            Storage.setItem((key + "-part" + index), compress(part));
        });
        Storage.setItem(key, ("" + store.length));
    }
    catch (error) {
        console.log("Could not save store : ", key, data.length);
        console.log(error.message);
        throw (error);
    }
};

export const clearAll = async () => {    
    Storage.clearAll();
}

export const loadJPGBase64 = async (key: string): Promise<string | null> => {
    try {
        return AsyncStorage.getItem(key);
    }
    catch (error) {
        console.log("Could not get [" + key + "] from store.");
        console.log(error);
        return null;
    }
};

export const saveJPGBase64 = async (key: string, data: string) => {
    try {
        await removeItem(key);
        console.log(navigator.storage.estimate())
    }
    catch (error) {
        console.log("Could not save store : ", key, data.length);
        console.log(error.message);
        throw (error);
    }
};

export const removeItem = async (key: string) => {
    try {
        key = keyNew(key);
        let numberOfParts: string | number = await Storage.getItem(key);
        if (typeof (numberOfParts) !== 'undefined' && numberOfParts !== null) {
            // @ts-ignore
            numberOfParts = parseInt(numberOfParts);
            for (let i = 0; i < numberOfParts; i++) { Storage.removeItem(key + "-part" + i); }
            Storage.setItem(key, ("0"));
        }
        Storage.removeItem(key);
    }
    catch (error) {
        console.log("Could not clear store : ");
        console.log(error.message);
    }
};

export async function load(key: string): Promise<any | null> {
    try {
        console.log(`storage.load ${key} 1`)
        const almostThere = await getItem(key)
        console.log(`storage.load ${key} 2`)
        const obj = JSON.parse(almostThere)
        console.log(`storage.load parse ${key} 2`)
        return obj
    } catch {
        return null
    }
}

export async function save(key: string, value: any): Promise<boolean> {
    try {
         console.log(`storage.save ${key} 1`)
         const str = JSON.stringify(value);
         console.log(`storage.save ${key} 2`)
         await setItem(key, str)
         console.log(`storage.save ${key} 3`)
         return true
    } catch {
        return false
    }
}

// ==========================================
// 4. SESSION STORAGE (AuthenticationStore)
// ==========================================

export async function loadSession(key: string): Promise<any | null> {
    try {
        if (Platform.OS === "web") {
            // En web usa la misma base IndexedDB (SimpleIDB con load)
            const fromMain = await load(key);
            if (fromMain) return fromMain;
            const direct = await SimpleIDB.getString("v3-" + key) || await SimpleIDB.getString(key);
            if (direct) return typeof direct === "string" ? JSON.parse(direct) : direct;
            return null;
        } else {
            // En mobile usa MMKV en la carpeta /cache/
            let val = sessionCacheDB.getString("v3-" + key) || sessionCacheDB.getString(key);
            if (!val) return null;
            return typeof val === "string" ? JSON.parse(val) : val;
        }
    } catch (e) {
        console.log(`storage.loadSession error for [${key}]:`, e);
        return null;
    }
}

export async function saveSession(key: string, value: any): Promise<boolean> {
    try {
        if (Platform.OS === "web") {
            // En web guarda en la misma base IndexedDB (SimpleIDB con save)
            return await save(key, value);
        } else {
            // En mobile guarda en MMKV de caché
            sessionCacheDB.set("v3-" + key, JSON.stringify(value));
            return true;
        }
    } catch (e) {
        console.log(`storage.saveSession error for [${key}]:`, e);
        return false;
    }
}

export async function removeSession(key: string): Promise<boolean> {
    try {
        if (Platform.OS === "web") {
            await removeItem(key);
            await SimpleIDB.delete("v3-" + key);
            await SimpleIDB.delete(key);
        } else {
            sessionCacheDB.delete("v3-" + key);
            sessionCacheDB.delete(key);
        }
        return true;
    } catch {
        return false;
    }
}

// ==========================================
// 5. LOCAL STORAGE (Navegación / Vistas)
// ==========================================

export async function loadLocal(key: string): Promise<any | null> {
    try {
        if (Platform.OS === "web") {
            // En web: persistido en la misma base IndexedDB
            let val = await SimpleIDB.getString(key);
            if (!val) {
                val = await SimpleIDB.getString("v3-" + key);
            }
            if (val) {
                return typeof val === "string" ? JSON.parse(val) : val;
            }
            // Migración única: si existía en AsyncStorage, migrar a IndexedDB y limpiar AsyncStorage
            const legacy = await AsyncStorage.getItem(key);
            if (legacy) {
                await AsyncStorage.removeItem(key);
                await SimpleIDB.set(key, legacy);
                return JSON.parse(legacy);
            }
            return null;
        } else {
            // En mobile: en sessionCacheDB (cachePath)
            let val = sessionCacheDB ? sessionCacheDB.getString(key) : null;
            if (val) {
                return typeof val === "string" ? JSON.parse(val) : val;
            }
            // Migración única: si existía en AsyncStorage, migrar a sessionCacheDB y limpiar AsyncStorage
            const legacy = await AsyncStorage.getItem(key);
            if (legacy) {
                await AsyncStorage.removeItem(key);
                if (sessionCacheDB) sessionCacheDB.set(key, legacy);
                return JSON.parse(legacy);
            }
            return null;
        }
    } catch {
        return null;
    }
}

export async function saveLocal(key: string, value: any): Promise<boolean> {
    try {
        const str = JSON.stringify(value);
        if (Platform.OS === "web") {
            await SimpleIDB.set(key, str);
        } else {
            if (sessionCacheDB) sessionCacheDB.set(key, str);
            // No duplicar en AsyncStorage para que "Borrar Caché" de Android sea 100% efectivo
        }
        return true;
    } catch {
        return false;
    }
}

export async function removeLocal(key: string): Promise<boolean> {
    try {
        if (Platform.OS === "web") {
            await SimpleIDB.delete(key);
            await SimpleIDB.delete("v3-" + key);
        } else {
            if (sessionCacheDB) sessionCacheDB.delete(key);
        }
        await AsyncStorage.removeItem(key);
        return true;
    } catch {
        return false;
    }
}

// ==========================================
// 6. LIMPIEZA SEGURA DE SESIÓN Y NAVEGACIÓN (Rescate)
// ==========================================

/**
 * Limpia el usuario logueado y el estado de navegación sin tocar
 * formularios, ítems offline ni ningún dato de negocio.
 */
export async function clearSessionAndNavigation(): Promise<void> {
    try {
        console.warn("Ejecutando clearSessionAndNavigation (conservando datos offline de negocio)...");
        // 1. Limpiar usuario y sus partes de IndexedDB / Storage principal
        await removeItem("authStore-v1");
        await removeItem("v3-authStore-v1");
        await SimpleIDB.delete("authStore-v1");
        await SimpleIDB.delete("v3-authStore-v1");

        // 2. Limpiar navegación de IndexedDB / Storage principal
        await removeItem("HOME_NAVIGATION_STATE");
        await removeItem("v3-HOME_NAVIGATION_STATE");
        await SimpleIDB.delete("HOME_NAVIGATION_STATE");
        await SimpleIDB.delete("v3-HOME_NAVIGATION_STATE");

        // 3. Limpiar sessionCacheDB si estamos en mobile
        if (Platform.OS !== "web" && sessionCacheDB) {
            try {
                sessionCacheDB.clearAll();
            } catch (e) {}
        }

        // 4. Limpiar AsyncStorage / localStorage
        await AsyncStorage.removeItem("HOME_NAVIGATION_STATE");
        await AsyncStorage.removeItem("v3-HOME_NAVIGATION_STATE");
        await AsyncStorage.removeItem("authStore-v1");
        await AsyncStorage.removeItem("v3-authStore-v1");

        if (typeof window !== "undefined") {
            if (window.localStorage) {
                window.localStorage.removeItem("HOME_NAVIGATION_STATE");
                window.localStorage.removeItem("v3-HOME_NAVIGATION_STATE");
                window.localStorage.removeItem("fromBuilder");
                for (let i = window.localStorage.length - 1; i >= 0; i--) {
                    const k = window.localStorage.key(i);
                    if (k && (k.startsWith("cache_session_") || k.includes("HOME_NAVIGATION_STATE") || k.includes("authStore"))) {
                        window.localStorage.removeItem(k);
                    }
                }
            }
            if (window.sessionStorage) {
                window.sessionStorage.clear();
            }
        }
        console.log("Sesión y navegación restablecidas con éxito (datos offline conservados).");
    } catch (e) {
        console.error("Error al ejecutar clearSessionAndNavigation:", e);
    }
}

// ==========================================
// 7. AUTO-RESCATE EN LA WEB VÍA PARÁMETRO DE URL (?resetNav=1 o ?safeMode=1)
// ==========================================
if (Platform.OS === "web" && typeof window !== "undefined" && window.location) {
    try {
        const urlParams = new URLSearchParams(window.location.search);
        if (
            urlParams.has("resetNav") ||
            urlParams.has("reset") ||
            urlParams.has("safeMode") ||
            urlParams.has("clearSession")
        ) {
            console.warn("Parámetro de rescate detectado en URL. Limpiando sesión y navegación...");
            clearSessionAndNavigation();
            const cleanUrl = window.location.pathname;
            window.history.replaceState({}, document.title, cleanUrl);
        }
    } catch (e) {
        console.error("Error procesando parámetro de rescate en URL:", e);
    }
}

export function localStorageSpace() {
    let allStrings = '';
    if (typeof window !== "undefined" && window.localStorage) {
        for (const key of Object.keys(window.localStorage)) {
            allStrings += window.localStorage[key];
        }
    }
    return allStrings ? (3 + ((allStrings.length * 16) / (8 * 1024))).toFixed(0) + ' KB' : 'Empty (0 KB)';
};
